import { useState, type FormEvent } from "react";
import { Plus, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { useCancelPo, useCreatePo, useProducts, useReceivePo, useSubmitPo, useVendors } from "./hooks";
import { PO_STATUS_LABELS, poTone } from "./labels";
import type { PurchaseOrderDetail } from "./api";

/**
 * One dialog, two modes: `existing === null` creates a draft PO with dynamic
 * lines; otherwise it shows the PO and its lifecycle actions (submit, receive,
 * cancel) driven by the PO's current status.
 */
export function PurchaseOrderDialog({
  open,
  onClose,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  existing: PurchaseOrderDetail | null;
}) {
  if (!open) return null;
  return existing ? (
    <PoDetail po={existing} onClose={onClose} />
  ) : (
    <PoCreate onClose={onClose} />
  );
}

function PoCreate({ onClose }: { onClose: () => void }) {
  const create = useCreatePo();
  const { data: vendors } = useVendors({ page_size: 100 }, { enabled: true });
  const { data: products } = useProducts({ page_size: 200, status: "active" }, { enabled: true });

  const [vendorId, setVendorId] = useState("");
  const [expected, setExpected] = useState("");
  const [lines, setLines] = useState<{ product_id: string; qty: string; cost: string }[]>([
    { product_id: "", qty: "1", cost: "" },
  ]);
  const [error, setError] = useState<string | null>(null);

  const setLine = (i: number, k: "product_id" | "qty" | "cost", v: string) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const clean = lines
      .filter((l) => l.product_id && Number(l.qty) > 0)
      .map((l) => ({
        product_id: l.product_id,
        quantity_ordered: Number(l.qty),
        unit_cost: l.cost.trim() || null,
      }));
    if (!vendorId || clean.length === 0) {
      setError("Pick a vendor and at least one product line.");
      return;
    }
    try {
      await create.mutateAsync({
        vendor_id: vendorId,
        expected_date: expected || null,
        lines: clean,
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the PO.");
    }
  }

  return (
    <Dialog open onClose={onClose} title="New purchase order">
      <form onSubmit={submit} className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vendor" htmlFor="po_vendor">
            <Select id="po_vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">Select a vendor…</option>
              {(vendors?.items ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Expected date" htmlFor="po_expected">
            <Input id="po_expected" type="date" value={expected} onChange={(e) => setExpected(e.target.value)} />
          </Field>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Lines</div>
          {lines.map((l, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="flex-1">
                <Select
                  aria-label={`Product ${i + 1}`}
                  value={l.product_id}
                  onChange={(e) => setLine(i, "product_id", e.target.value)}
                >
                  <option value="">Product…</option>
                  {(products?.items ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.sku})
                    </option>
                  ))}
                </Select>
              </div>
              <Input
                aria-label={`Quantity ${i + 1}`}
                type="number"
                min="1"
                className="w-20"
                value={l.qty}
                onChange={(e) => setLine(i, "qty", e.target.value)}
              />
              <Input
                aria-label={`Unit cost ${i + 1}`}
                type="number"
                min="0"
                step="0.01"
                className="w-28"
                placeholder="₹ cost"
                value={l.cost}
                onChange={(e) => setLine(i, "cost", e.target.value)}
              />
              <button
                type="button"
                aria-label={`Remove line ${i + 1}`}
                className="rounded-md p-2 hover:bg-accent disabled:opacity-40"
                disabled={lines.length === 1}
                onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setLines((ls) => [...ls, { product_id: "", qty: "1", cost: "" }])}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add line
          </Button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            Create draft
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function PoDetail({ po, onClose }: { po: PurchaseOrderDetail; onClose: () => void }) {
  const submit = useSubmitPo();
  const receive = useReceivePo();
  const cancel = useCancelPo();
  const [error, setError] = useState<string | null>(null);

  const canSubmit = po.status === "draft";
  const canReceive = po.status === "ordered" || po.status === "partially_received";
  const canCancel = po.status !== "received" && po.status !== "cancelled";

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Action failed.");
    }
  }

  return (
    <Dialog open onClose={onClose} title={po.po_number}>
      <div className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{po.vendor_name}</span>
          <Badge tone={poTone(po.status)}>{PO_STATUS_LABELS[po.status]}</Badge>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Product</th>
                <th className="px-3 py-2 font-medium">Ordered</th>
                <th className="px-3 py-2 font-medium">Received</th>
              </tr>
            </thead>
            <tbody>
              {po.lines.map((l) => (
                <tr key={l.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    {l.product_name}
                    <span className="text-xs text-muted-foreground"> ({l.product_sku})</span>
                  </td>
                  <td className="px-3 py-2 tabular-nums">{l.quantity_ordered}</td>
                  <td className="px-3 py-2 tabular-nums">{l.quantity_received}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-right text-sm">
          Total: <span className="font-medium">₹{po.total_cost}</span>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          {canCancel && (
            <Button variant="destructive" disabled={cancel.isPending} onClick={() => void run(() => cancel.mutateAsync(po.id))}>
              Cancel PO
            </Button>
          )}
          {canSubmit && (
            <Button variant="outline" disabled={submit.isPending} onClick={() => void run(() => submit.mutateAsync(po.id))}>
              Submit (order)
            </Button>
          )}
          {canReceive && (
            <Button disabled={receive.isPending} onClick={() => void run(() => receive.mutateAsync({ id: po.id }))}>
              Receive all outstanding
            </Button>
          )}
          {!canSubmit && !canReceive && !canCancel && (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
